import { differenceInCalendarDays, format, parseISO } from 'date-fns';
import { TODAY, resolvedStatuses, statusOptions, teamMembers } from '../data/conditions';
import type { Condition, ConditionStatus, EscalationLevel, SortKey, SortState, TeamMember } from '../types/conditions';

function sortValue(c: Condition, key: SortKey): string | number | undefined {
  switch (key) {
    case 'name':
      return c.name.toLowerCase();
    case 'status':
      return statusOptions.findIndex((o) => o.value === c.status);
    case 'createdAt':
      return c.createdAt;
    case 'requestedAt':
      return c.requestedAt;
    case 'eta':
      if (isResolved(c.status)) return c.clearedAt;
      return etaApplies(c.status) ? c.eta : undefined;
    case 'assignee':
      return getMember(c.assigneeId).name.toLowerCase();
  }
}

/** Sorts a copy; empty values always sink to the bottom regardless of direction. */
export function sortConditions(list: Condition[], sort: SortState | null): Condition[] {
  if (!sort) return list;
  const dir = sort.direction === 'asc' ? 1 : -1;
  return [...list].sort((a, b) => {
    const av = sortValue(a, sort.key);
    const bv = sortValue(b, sort.key);
    if (av === undefined && bv === undefined) return 0;
    if (av === undefined) return 1;
    if (bv === undefined) return -1;
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/** Shared column layout for the condition table header + rows. */
export const conditionRowGrid =
  'grid grid-cols-[24px_minmax(200px,1.6fr)_minmax(132px,0.8fr)_minmax(104px,0.7fr)_minmax(104px,0.7fr)_minmax(112px,0.8fr)_minmax(64px,0.5fr)_212px] items-center gap-x-4';

export function isResolved(status: ConditionStatus): boolean {
  return resolvedStatuses.includes(status);
}

export function documentCount(condition: Condition): number {
  return condition.documents + (condition.linkedDocumentIds?.length ?? 0);
}

export function formatDate(value?: string): string {
  if (!value) return '—';
  return format(parseISO(value), 'MMM d, yyyy');
}

export function formatDateTime(value: string): string {
  return format(parseISO(value), "MMM d, yyyy '·' h:mm a");
}

export function daysOpen(condition: Condition): number {
  const start = condition.requestedAt ?? condition.createdAt;
  return differenceInCalendarDays(parseISO(TODAY), parseISO(start));
}

/** ETA only matters while we're waiting on the item (New / Requested). Once submitted or in review it's satisfied. */
export function etaApplies(status: ConditionStatus): boolean {
  return status === 'new' || status === 'requested';
}

/** Days past the ETA for a condition still awaiting documents; 0 when not overdue. */
export function daysPastEta(condition: Condition): number {
  if (!condition.eta || !etaApplies(condition.status)) return 0;
  return Math.max(0, differenceInCalendarDays(parseISO(TODAY), parseISO(condition.eta)));
}

/** No ETA and no notes after 2 days → processor; day 3+ → AVP of Processing. */
export function getEscalation(condition: Condition): EscalationLevel {
  if (!etaApplies(condition.status)) return 'none';
  if (condition.eta || condition.comments.length > 0) return 'none';
  const days = daysOpen(condition);
  if (days >= 3) return 'avp';
  if (days >= 2) return 'processor';
  return 'none';
}

export function getMember(id: string): TeamMember {
  return teamMembers.find((m) => m.id === id) ?? teamMembers[0];
}
